import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
    Mail,
    Lock,
    LogIn,
    ArrowRight,
    ShieldCheck,
    Sparkles,
    GraduationCap,
    Users,
    BriefcaseBusiness,
    Eye,
    EyeOff
} from "lucide-react";

import { loginUser } from "../services/authService";

/* ---------------------------------------------------------------
   Styles (CertED theme)
---------------------------------------------------------------- */

const loginStyles = `
@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@700;800&display=swap");

/* =========================================================
   CERTED TECHNOLOGIES - LOGIN PAGE
   ========================================================= */

.login-page {
    /* Theme colours */
    --certed-purple: #3e0f8d;
    --certed-violet: #9564dd;
    --certed-yellow: #e4da72;

    width: 100%;
    min-height: 100vh;
    display: flex;
    align-items: center;
    padding: 40px 6%;
    position: relative;
    overflow: hidden;

    background:
        radial-gradient(circle at 8% 10%, rgba(149, 100, 221, 0.15), transparent 25%),
        radial-gradient(circle at 92% 85%, rgba(228, 218, 114, 0.16), transparent 24%),
        #eeeeee;
}

.login-page *,
.login-page *::before,
.login-page *::after {
    box-sizing: border-box;
}

.login-page input,
.login-page button {
    font-family: inherit;
}


/* ---------- Background shapes ---------- */

.login-bg-shape {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
}

.login-shape-one {
    width: 420px;
    height: 420px;
    top: -230px;
    right: -120px;
    background: rgba(62, 15, 141, 0.05);
}

.login-shape-two {
    width: 260px;
    height: 260px;
    bottom: -160px;
    left: -80px;
    background: rgba(149, 100, 221, 0.08);
}

.login-shape-three {
    width: 90px;
    height: 90px;
    top: 20%;
    left: 4%;
    border: 15px solid rgba(228, 218, 114, 0.3);
}


/* ---------- Layout ---------- */

.login-wrapper {
    width: 100%;
    max-width: 1240px;
    margin: auto;
    display: grid;
    grid-template-columns: 1fr 0.82fr;
    align-items: center;
    gap: 80px;
    position: relative;
    z-index: 2;
}


/* =========================================================
   LEFT SIDE
   ========================================================= */

.login-intro {
    max-width: 650px;
}

.login-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    margin-bottom: 20px;
    border-radius: 50px;
    background: rgba(62, 15, 141, 0.08);
    color: var(--certed-purple);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.4px;
}

.login-badge svg {
    width: 15px;
    height: 15px;
}

.login-intro-text h1 {
    margin: 0 0 20px;
    font-family: "Manrope", sans-serif;
    font-size: clamp(42px, 5vw, 65px);
    font-weight: 800;
    line-height: 1.03;
    color: var(--certed-purple);
}

.login-intro-text h1 span {
    color: var(--certed-violet);
}

.login-intro-text > p {
    max-width: 570px;
    margin: 0 0 28px;
    color: #665e70;
    font-size: 14px;
    line-height: 1.8;
}


/* ---------- Visual ---------- */

.login-visual {
    width: 100%;
    height: 250px;
    margin-bottom: 28px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.login-orbit {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(62, 15, 141, 0.12);
}

.login-orbit.orbit-one   { width: 245px; height: 245px; }
.login-orbit.orbit-two   { width: 180px; height: 180px; }
.login-orbit.orbit-three { width: 120px; height: 120px; }

.login-main-icon {
    width: 90px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 3;
    border-radius: 25px;
    color: #ffffff;
    background: linear-gradient(135deg, var(--certed-purple), var(--certed-violet));
    box-shadow:
        0 20px 45px rgba(62, 15, 141, 0.25),
        0 0 0 10px rgba(62, 15, 141, 0.05);
    animation: loginPulse 3s ease-in-out infinite;
}

.login-main-icon svg {
    width: 42px;
    height: 42px;
}

.login-floating {
    position: absolute;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 13px;
    background: #ffffff;
    border: 1px solid rgba(62, 15, 141, 0.07);
    border-radius: 11px;
    box-shadow: 0 10px 30px rgba(62, 15, 141, 0.1);
    animation: loginFloat 4s ease-in-out infinite;
}

.login-floating svg {
    width: 17px;
    height: 17px;
    color: var(--certed-purple);
}

.login-floating-one   { top: 15px; left: 5%; }
.login-floating-two   { top: 70px; right: 3%; animation-delay: 0.7s; }
.login-floating-three { bottom: 15px; left: 18%; animation-delay: 1.4s; }

/* Small info card, bottom-right of the visual */
.login-mini-card {
    position: absolute;
    right: 4%;
    bottom: 8px;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 13px;
    background: #ffffff;
    border: 1px solid rgba(62, 15, 141, 0.06);
    border-radius: 12px;
    box-shadow: 0 8px 22px rgba(62, 15, 141, 0.06);
}

.login-mini-card svg {
    width: 19px;
    height: 19px;
    flex-shrink: 0;
    color: var(--certed-violet);
}

.login-mini-card strong {
    display: block;
    color: var(--certed-purple);
    font-size: 11px;
    font-weight: 800;
}

.login-mini-card span {
    display: block;
    color: #8b8492;
    font-size: 9px;
}

@keyframes loginPulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.04); }
}

@keyframes loginFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-8px); }
}


/* ---------- Features ---------- */

.login-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 11px;
}

.login-feature {
    min-height: 92px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(62, 15, 141, 0.07);
    border-radius: 14px;
    box-shadow: 0 7px 22px rgba(62, 15, 141, 0.05);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.login-feature:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 30px rgba(62, 15, 141, 0.1);
}

.login-feature-icon {
    width: 30px;
    height: 30px;
    margin-bottom: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    background: rgba(149, 100, 221, 0.11);
    color: var(--certed-purple);
}

.login-feature-icon svg {
    width: 16px;
    height: 16px;
}

.login-feature h4 {
    margin: 0;
    color: #3d3349;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.4;
}

.login-feature p {
    margin: 4px 0 0;
    color: #8b8492;
    font-size: 9px;
    line-height: 1.5;
}


/* =========================================================
   LOGIN CARD
   ========================================================= */

.login-card {
    width: 100%;
    max-width: 430px;
    margin-left: auto;
    padding: 34px;
    position: relative;
    background: #ffffff;
    border: 1px solid rgba(62, 15, 141, 0.08);
    border-radius: 24px;
    box-shadow: 0 25px 65px rgba(42, 16, 69, 0.12);
}

/* Brand-colour strip on top of the card */
.login-card::before {
    content: "";
    position: absolute;
    top: 0;
    left: 40px;
    right: 40px;
    height: 4px;
    border-radius: 0 0 10px 10px;
    background: linear-gradient(90deg, var(--certed-purple), var(--certed-violet), var(--certed-yellow));
}

.login-card-header {
    margin-bottom: 26px;
    text-align: center;
}

.login-icon-box {
    width: 54px;
    height: 54px;
    margin: 0 auto 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    background: rgba(62, 15, 141, 0.08);
    color: var(--certed-purple);
}

.login-icon-box svg {
    width: 25px;
    height: 25px;
}

.login-card-header span {
    display: block;
    margin-bottom: 7px;
    color: var(--certed-violet);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.4px;
}

.login-card-header h2 {
    margin: 0 0 7px;
    color: var(--certed-purple);
    font-family: "Manrope", sans-serif;
    font-size: 25px;
    font-weight: 800;
}

.login-card-header p {
    margin: 0;
    color: #89818f;
    font-size: 11px;
    line-height: 1.5;
}


/* ---------- Form ---------- */

.login-form {
    display: flex;
    flex-direction: column;
    gap: 17px;
}

.login-field {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.login-field label {
    color: #453a50;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.3px;
}

.login-input-wrapper {
    width: 100%;
    height: 47px;
    display: flex;
    align-items: center;
    border: 1px solid #ddd7e5;
    border-radius: 11px;
    background: #faf9fc;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.login-input-wrapper:focus-within {
    background: #ffffff;
    border-color: var(--certed-violet);
    box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.1);
}

.login-input-wrapper > svg {
    width: 17px;
    height: 17px;
    margin-left: 13px;
    flex-shrink: 0;
    color: #8b8197;
}

.login-input-wrapper input {
    flex: 1;
    width: 100%;
    height: 100%;
    padding: 0 12px;
    border: none;
    outline: none;
    background: transparent;
    color: #30263a;
    font-size: 12px;
}

.login-input-wrapper input::placeholder {
    color: #aaa2b1;
}

.password-toggle {
    width: 35px;
    height: 35px;
    margin-right: 5px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #8b8197;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
}

.password-toggle:hover {
    background: rgba(149, 100, 221, 0.09);
    color: var(--certed-purple);
}

.password-toggle svg {
    width: 16px;
    height: 16px;
}

.login-security {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 11px;
    border-radius: 9px;
    background: rgba(228, 218, 114, 0.15);
    color: #665e2a;
    font-size: 9px;
    font-weight: 600;
    line-height: 1.4;
}

.login-security svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: #817615;
}


/* ---------- Submit ---------- */

.login-submit {
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    border: none;
    border-radius: 11px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    background: linear-gradient(135deg, var(--certed-purple) 0%, #6b32b6 55%, var(--certed-violet) 100%);
    box-shadow: 0 12px 27px rgba(62, 15, 141, 0.22);
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 16px 34px rgba(62, 15, 141, 0.3);
}

.login-submit:active:not(:disabled) {
    transform: translateY(0);
}

.login-submit:disabled {
    cursor: not-allowed;
    opacity: 0.72;
}

.login-submit svg {
    width: 16px;
    height: 16px;
}

.login-spinner {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    animation: loginSpin 0.75s linear infinite;
}

@keyframes loginSpin {
    to { transform: rotate(360deg); }
}


/* ---------- Register + footer ---------- */

.login-register {
    margin-top: 2px;
    text-align: center;
    color: #817987;
    font-size: 10px;
}

.login-register a {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 4px;
    color: var(--certed-purple);
    font-weight: 800;
    text-decoration: none;
    transition: color 0.2s ease;
}

.login-register a:hover {
    color: var(--certed-violet);
}

.login-register a svg {
    width: 13px;
    height: 13px;
}

.login-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 22px;
    padding-top: 17px;
    border-top: 1px solid #eeeaf1;
    color: #9a939f;
    font-size: 8px;
    font-weight: 600;
}

/* Keyboard focus */
.login-submit:focus-visible,
.password-toggle:focus-visible,
.login-register a:focus-visible {
    outline: 3px solid rgba(149, 100, 221, 0.45);
    outline-offset: 2px;
}


/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 1050px) {
    .login-wrapper {
        grid-template-columns: 1fr;
        max-width: 750px;
        gap: 35px;
    }

    .login-intro {
        max-width: 750px;
        margin: auto;
        text-align: center;
    }

    .login-intro-text > p {
        margin-left: auto;
        margin-right: auto;
    }

    .login-visual {
        max-width: 600px;
        margin: 0 auto 25px;
    }

    .login-features {
        max-width: 650px;
        margin: auto;
        text-align: left;
    }

    .login-card {
        max-width: 470px;
        margin: auto;
    }
}

@media (max-width: 700px) {
    .login-page {
        padding: 24px 5%;
    }

    .login-features {
        grid-template-columns: 1fr;
    }

    .login-feature {
        min-height: 0;
    }

    .login-visual {
        display: none;
    }
}

@media (max-width: 480px) {
    .login-card {
        padding: 28px 20px;
    }

    /* 16px stops iOS from zooming into the field */
    .login-input-wrapper input {
        font-size: 16px;
    }

    .login-card-footer {
        flex-direction: column;
        text-align: center;
    }
}

@media (prefers-reduced-motion: reduce) {
    .login-main-icon,
    .login-floating {
        animation: none;
    }
}
`;

/* ---------------------------------------------------------------
   Constants
---------------------------------------------------------------- */

// Where each role lands after a successful login.
const ROLE_ROUTES = {
    ADMIN: "/dashboard",
    TRAINER: "/trainer-dashboard",
    PARTNER: "/partner-dashboard",
    RECRUITER: "/partner-dashboard",
    EMPLOYEE: "/employee-dashboard"
};

// Feature list shown on the left side of the page.
const FEATURES = [
    {
        Icon: GraduationCap,
        titleKey: "login.featureTrainingTitle",
        descriptionKey: "login.featureTrainingDescription"
    },
    {
        Icon: Users,
        titleKey: "login.featureNetworkTitle",
        descriptionKey: "login.featureNetworkDescription"
    },
    {
        Icon: BriefcaseBusiness,
        titleKey: "login.featureCareerTitle",
        descriptionKey: "login.featureCareerDescription"
    }
];

/* ---------------------------------------------------------------
   Helpers
---------------------------------------------------------------- */

// Reads the token from any of the field names the backend may use.
const getToken = (data) => data.token || data.accessToken || data.jwt;

// Builds the user object we keep in localStorage.

const buildUser = (data, fallbackEmail) => ({
    id: data.id,
    name: data.name || data.fullName,
    email: data.email || fallbackEmail,
    role: String(data.role || "").trim().toUpperCase(),
    status: String(data.status || "").trim().toUpperCase()
});


// Picks the most useful error message from a failed request.
const getErrorMessage = (error, t) => {
    if (!error.response) {
        return t("login.serverError");
    }

    const { data } = error.response;

    if (typeof data === "string") {
        return data;
    }

    return data?.message || t("login.invalidCredentials");
};

// SweetAlert with CertED theme.
const showAlert = (options) =>
    Swal.fire({
        confirmButtonColor: "#3e0f8d",
        confirmButtonText: "OK",
        ...options
    });

/* ---------------------------------------------------------------
   Component
---------------------------------------------------------------- */

function Login() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

  

const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const email = form.email.trim();
    const { password } = form;

    if (!email || !password) {
        await showAlert({
            icon: "warning",
            title: t("login.validation"),
        });
        return;
    }

    setLoading(true);

    try {
      const response = await loginUser({ email, password });

console.log("LOGIN RESPONSE:", response);
console.log("LOGIN RESPONSE DATA:", response?.data);

const data = response?.data || {};

        // Support common backend response formats.
        const payload = data.user || data;
        const token = getToken(data) || getToken(payload);

        const user = buildUser(payload, email);
        const role = String(
            payload.role ||
            payload.userRole ||
            payload.roles?.[0] ||
            ""
        )
            .replace(/^ROLE_/, "")
            .trim()
            .toUpperCase();

        const status = String(payload.status || "")
            .trim()
            .toUpperCase();

        if (!role) {
            throw new Error(
                "The login response does not contain a user role."
            );
        }

        // A token is required because Navbar uses it to determine login.
        if (!token) {
            throw new Error(
                "The login response does not contain an authentication token. Check your backend response."
            );
        }

        // Partner approval check.
        if (role === "PARTNER" && status !== "APPROVED") {
            localStorage.removeItem("token");
            localStorage.removeItem("user");

            window.dispatchEvent(new Event("authChanged"));

            navigate("/partner-verification", {
                replace: true,
                state: {
                    status: status || "PENDING",
                },
            });

            await showAlert({
                icon: status === "REJECTED" ? "error" : "info",
                title:
                    status === "REJECTED"
                        ? "Partner Registration Rejected"
                        : "Account Pending Approval",
                text:
                    status === "REJECTED"
                        ? "Please contact the administrator regarding your registration."
                        : "Your partner account is awaiting administrator approval.",
            });

            return;
        }

        const destination = ROLE_ROUTES[role];

        if (!destination) {
            throw new Error(
                `Login is not configured for role: ${role}`
            );
        }

        // Save the actual authentication token and normalized user details.
        const savedUser = {
            ...payload,
            ...user,
            role,
        };

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(savedUser));

        // Notify Navbar and other components in the same tab.
        window.dispatchEvent(new Event("authChanged"));

        await showAlert({
            icon: "success",
            title: t("login.success"),
            confirmButtonText: "OK",
        });

        navigate(destination, { replace: true });
    } catch (error) {
        console.error(
            "Login failed:",
            error.response?.data || error.message
        );

        await showAlert({
            icon: "error",
            title: "Login Failed",
            text:
                error.response?.data?.message ||
                error.message ||
                t("login.invalidCredentials"),
        });
    } finally {
        setLoading(false);
    }
};



    return (
        <>
            <style>{loginStyles}</style>

            <div className="login-page">
                {/* Background decorations */}
                <div className="login-bg-shape login-shape-one" />
                <div className="login-bg-shape login-shape-two" />
                <div className="login-bg-shape login-shape-three" />

                <div className="login-wrapper">
                    {/* ================= LEFT SIDE ================= */}
                    <div className="login-intro">
                        <div className="login-badge">
                            <Sparkles size={15} />
                            <span>{t("login.welcomeBadge")}</span>
                        </div>

                        <div className="login-intro-text">
                            <h1>
                                {t("login.heroTitle1")}
                                <span>{t("login.heroHighlight1")}</span>
                                <br />
                                {t("login.heroTitle2")}
                                <span>{t("login.heroHighlight2")}</span>
                            </h1>

                            <p>{t("login.heroDescription")}</p>
                        </div>

                        {/* Visual */}
                        <div className="login-visual">
                            <div className="login-orbit orbit-one" />
                            <div className="login-orbit orbit-two" />
                            <div className="login-orbit orbit-three" />

                            <div className="login-main-icon">
                                <GraduationCap size={48} />
                            </div>

                            <div className="login-floating login-floating-one">
                                <Users size={21} />
                            </div>

                            <div className="login-floating login-floating-two">
                                <BriefcaseBusiness size={21} />
                            </div>

                            <div className="login-floating login-floating-three">
                                <ShieldCheck size={20} />
                            </div>

                            <div className="login-mini-card">
                                <Sparkles size={19} />

                                <div>
                                    <strong>{t("login.platformTitle")}</strong>
                                    <span>{t("login.platformSubtitle")}</span>
                                </div>
                            </div>
                        </div>

                        {/* Features */}
                        <div className="login-features">
                            {FEATURES.map(({ Icon, titleKey, descriptionKey }) => (
                                <div className="login-feature" key={titleKey}>
                                    <div className="login-feature-icon">
                                        <Icon size={18} />
                                    </div>

                                    <div>
                                        <h4>{t(titleKey)}</h4>
                                        <p>{t(descriptionKey)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ================= LOGIN CARD ================= */}
                    <div className="login-card">
                        <div className="login-card-header">
                            <div className="login-icon-box">
                                <LogIn size={23} />
                            </div>

                            <div>
                                <span>{t("login.accountLogin")}</span>
                                <h2>{t("login.welcomeBack")}</h2>
                                <p>{t("login.signInDescription")}</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="login-form">
                            {/* Email */}
                            <div className="login-field">
                                <label htmlFor="login-email">
                                    {t("login.emailLabel")}
                                </label>

                                <div className="login-input-wrapper">
                                    <Mail size={18} />
                                    <input
                                        id="login-email"
                                        type="email"
                                        name="email"
                                        placeholder={t("login.emailPlaceholder")}
                                        value={form.email}
                                        onChange={handleChange}
                                        autoComplete="email"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password */}
                            <div className="login-field">
                                <label htmlFor="login-password">
                                    {t("login.passwordLabel")}
                                </label>

                                <div className="login-input-wrapper">
                                    <Lock size={18} />
                                    <input
                                        id="login-password"
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder={t("login.passwordPlaceholder")}
                                        value={form.password}
                                        onChange={handleChange}
                                        autoComplete="current-password"
                                        required
                                    />

                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        aria-label={
                                            showPassword
                                                ? t("login.hidePassword")
                                                : t("login.showPassword")
                                        }
                                    >
                                        {showPassword
                                            ? <EyeOff size={17} />
                                            : <Eye size={17} />}
                                    </button>
                                </div>
                            </div>

                            {/* Security note */}
                            <div className="login-security">
                                <ShieldCheck size={16} />
                                <span>{t("login.securityMessage")}</span>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="login-submit"
                            >
                                {loading ? (
                                    <>
                                        <span className="login-spinner" />
                                        {t("login.loggingIn")}
                                    </>
                                ) : (
                                    <>
                                        {t("login.loginButton")}
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>

                            {/* Register */}
                            <div className="login-register">
                                <span>{t("login.noAccount")}</span>

                                <Link to="/trainer-register">
                                    {t("login.registerNow")}
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        </form>

                        <div className="login-card-footer">
                            <span>
                                © {new Date().getFullYear()} CertED Technologies
                            </span>
                            <span>{t("login.secureAccess")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Login;